import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { LogoComponent, logoUrlClub } from 'app/components/logo/logo.component';

import { CommonModule } from '@angular/common';
import { LineupComponent } from '../../../components/matchLive/lineUp/lineup.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { StatisticsService } from '../dashboard/statistics.service';
import { StorageService } from 'app/modules/auth/services/storage.service';
// import { Match } from 'app/modules/admin/match-calendar/models/match.interface';
import { Subject } from 'rxjs';
import { TeamService } from '../teams/team.service';
import { TranslocoModule } from '@ngneat/transloco';
import { takeUntil } from 'rxjs/operators';
interface Player {
    id: string;
    name: string;
    position: string | null;
    number: number | null;
    avatar?: string | null;
}

interface Match {
    id: string;
    opponent: string;
    opponentLogo_url: string;
    date: string;
    goals: number | null;
    opponent_goals: number | null;
    homeTeam: string;
    awayTeam: string;
    isHome: boolean;
    status: string;
    location: string;
    type: string;
}

interface UpcomingEvent {
    teamLogo_url: string;
    opponentLogo_url: string | null;
    type: string;
    id: string;
    opponent: string;
    date: Date;
    location: string;
    homeTeam: string;
    awayTeam: string;
    isHome: boolean;
}

interface Coach {
    id: string;
    name: string;
    title: string;
    avatar: string;
    description: string;
    gender: string; // Ensure gender is included
}

interface TeamData {
    upcomingEvents: UpcomingEvent[];
    teamInfo: {
        banner_url?: string | null;
        id: string;
        name: string;
        description: string;
        location: string;
        coach: string;
        email: string;
        phone: string;
        season: string;
        logo_url?: string;
    };
    players: Player[];
    coaches: Coach[];
    pastMatches: Match[];
    upcomingMatches: UpcomingEvent[];
    nextMatch: UpcomingEvent | null;
    lastMatchWithLineup?: any;
}

@Component({
    selector: 'app-team-public-view',
    standalone: true,
    imports: [
        CommonModule,
        LogoComponent,
        TranslocoModule,
        MatIconModule,
        MatButtonModule,
        LineupComponent,
        RouterModule,
    ],
    templateUrl: './team-public-view.component.html',
    styleUrls: ['./team-public-view.component.scss'],
})
export class TeamPublicViewComponent implements OnInit, OnDestroy {
    @Input() match: Match;
    private route = inject(ActivatedRoute);
    private teamService = inject(TeamService);
    private _destroyed = new Subject<void>();
    compositionAvailable: boolean = false;
    sport: string = '';
    team: TeamData | null = null;
    loading = true;
    error = false;
    lastMatchWithLineup: any = null;
    countdown: {
        days: number;
        hours: number;
        minutes: number;
        seconds: number;
    } = { days: 0, hours: 0, minutes: 0, seconds: 0 };
    countdownInterval?: any;
    currentCoachIndex = 0;
    constructor(
        private storageService: StorageService,
        private statisticsService: StatisticsService,
        private router: Router
    ) {}
    createEmptyMatch(): any {
        return {
            lineup: {
                formation: '',
                starting_players: [],
            },

            liveEvents: [],
        };
    }
    readonly teamLogoUrl: string = logoUrlClub;

    ngOnInit(): void {
        this.sport =
            this.storageService.getClubData()?.sport_name?.toLowerCase() ||
            'football';
        this.route.paramMap
            .pipe(takeUntil(this._destroyed))
            .subscribe((params) => {
                const teamId = params.get('id');
                if (teamId) {
                    this.loadTeam(teamId);
                }
            });

        this.statisticsService.trackMenuView(
            'equipes_publiques',
            'Équipes publiques',
            {
                source: 'direct_navigation',
            }
        );
    }

    ngOnDestroy(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
        this._destroyed.next();
        this._destroyed.complete();
    }

    private loadTeam(teamId: string): void {
        this.loading = true;
        this.teamService.getTeamPublicData(teamId).subscribe({
            next: (data: TeamData) => {
                console.log('Team Data:', data);
                this.team = {
                    ...data,
                    pastMatches: data.pastMatches.map((match) => ({
                        ...match,
                    })),
                };
                this.lastMatchWithLineup = data.lastMatchWithLineup;

                this.loading = false;
                if (data.nextMatch) {
                    this.startCountdown();
                }
            },
            error: (err) => {
                console.error('Error loading team data:', err);
                this.error = true;
                this.loading = false;
            },
        });
    }

    private startCountdown(): void {
        if (!this.team?.nextMatch) return;

        this.updateCountdown();
        this.countdownInterval = setInterval(() => {
            this.updateCountdown();
        }, 1000);
    }

    private updateCountdown(): void {
        if (!this.team?.nextMatch) return;

        const now = new Date().getTime();
        const matchTime = new Date(this.team.nextMatch.date).getTime();
        const difference = matchTime - now;

        if (difference > 0) {
            this.countdown.days = Math.floor(
                difference / (1000 * 60 * 60 * 24)
            );
            this.countdown.hours = Math.floor(
                (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
            );
            this.countdown.minutes = Math.floor(
                (difference % (1000 * 60 * 60)) / (1000 * 60)
            );
            this.countdown.seconds = Math.floor(
                (difference % (1000 * 60)) / 1000
            );
        } else {
            this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        }
    }

    getCurrentCoach(): Coach {
        return (
            this.team?.coaches[this.currentCoachIndex] || {
                id: '',
                name: 'No coach',
                title: '',
                avatar: '',
                description: '',
                gender: 'Male',
            }
        );
    }

    getCoachLogoUrl(coach: Coach): string {
        return coach.gender === 'Female'
            ? '/assets/images/logo/coach_feminin.png'
            : '/assets/images/logo/coach logo.png';
    }

    nextCoach(): void {
        if (this.team?.coaches) {
            this.currentCoachIndex =
                (this.currentCoachIndex + 1) % this.team.coaches.length;
        }
    }

    previousCoach(): void {
        if (this.team?.coaches) {
            this.currentCoachIndex =
                this.currentCoachIndex === 0
                    ? this.team.coaches.length - 1
                    : this.currentCoachIndex - 1;
        }
    }

    goToCoach(index: number): void {
        this.currentCoachIndex = index;
    }

    formatCountdownNumber(num: number): string {
        return num.toString().padStart(2, '0');
    }

    get upcomingEvents(): UpcomingEvent[] {
        return this.team?.upcomingEvents || [];
    }

    get pastMatches(): Match[] {
        return this.team?.pastMatches || [];
    }

    get players(): Player[] {
        if (this.coaches?.length && this.team?.players?.length) {
            const coachIds = this.coaches.map((coach) => coach.id);
            return this.team?.players.filter(
                (player) => !coachIds.includes(player.id)
            );
        }
        return this.team?.players || [];
    }

    get coaches(): Coach[] {
        return this.team?.coaches || [];
    }

    get upcomingMatch(): UpcomingEvent | null {
        return this.team?.nextMatch || null;
    }

    getEventTypeLabel(type: string): string {
        const typeLabels = {
            MATCH_AMICAL: 'Match amical',
            MATCH_ENTRE_NOUS: 'Match interne',
            MATH_DE_COUPE: 'Match de coupe',
            MATCH_CHAMPIONNAT: 'Match de championnat',
            TOURNOI: 'Tournoi',
            ENTRAINEMENT: 'Entraînement',
            EVENEMENT: 'Événement',
        };
        return typeLabels[type] || type;
    }

    formatPlayerName(fullName: string): string {
        const [firstName, lastName] = fullName.split(' ');
        return `${firstName} ${lastName ? lastName.charAt(0) + '.' : ''}`;
    }

    getPlayerInitials(fullName: string): string {
        const [firstName, lastName] = fullName.split(' ');
        return `${firstName.charAt(0)}${
            lastName ? lastName.charAt(0) : ''
        }`.toUpperCase();
    }

    formatMatchScore(match: Match): string {
        const teamGoals = match.goals ?? '';
        const opponentGoals = match.opponent_goals ?? '';
        return match.isHome
            ? `${teamGoals} - ${opponentGoals}` // Domicile: Senior  (gauche) vs Adversaire (droite)
            : `${opponentGoals} - ${teamGoals}`; // Extérieur: Adversaire (gauche) vs Senior (droite)
    }

    getMatchResult(match: Match): string {
        if (match.status === 'forfeit') {
            return 'forfeit';
        }
        if (match.goals !== null && match.opponent_goals !== null) {
            if (match.goals > match.opponent_goals) {
                return 'win';
            } else if (match.goals < match.opponent_goals) {
                return 'loss';
            }
        }
        return 'draw';
    }

 
    onGoToMatchDetail(matchId: string) {
         console.log('le match id', matchId);
        this.router.navigateByUrl(`/match-details?id=${matchId}`);
    }
    // onGoToMatchDetail(matchId: string) {
    //     console.log('le match id', matchId);
    //     this.router.navigate(['/match-details'], {
    //         queryParams: { id: matchId },
    //     });
    // }

    getMatchStatusLabel(status: string | null | undefined): string {
        if (!status || status === 'unknown') return '';
        if (this.sport !== 'basketball') {
            const labels: { [key: string]: string } = {
                win: 'Victoire',
                draw: 'Match nul',
                loss: 'Défaite',
                forfeit: 'Forfait',
            };

            return labels[status] || status;
        }
        if (this.sport === 'basketball' || this.sport === 'Basketball') {
            const labels: { [key: string]: string } = {
                win: 'Victoire',
                // draw: 'Match nul',
                loss: 'Défaite',
                forfeit: 'Forfait',
            };

            return labels[status] || status;
        }
    }
}
